import Module, { ModuleModalProps } from "@/components/module/Module";
import styles from "./ContentLayout.module.scss";
import { Box } from "@mui/material";
import { HelpProps, NoBackgroundProps, NoPaddingProps } from "@/models/content-layout";
import { MutableRefObject, ReactNode, RefObject, useState } from "react";
import { ContentLayoutContext } from "@/lib/Context";
import { classNames } from "@/lib/utils";
import { usePageTour } from "@/components/tour/usePageTour";

type Props = {
    allowFullscreen?: boolean;
    children: JSX.Element;
    filters?: JSX.Element;
    help?: HelpProps;
    helpActions?: { label: string; onClick: () => void }[];
    title: string | JSX.Element;
    modal?: ModuleModalProps;
    stockPage?: boolean;
    sonification?: MutableRefObject<{
        chart: unknown;
        container: RefObject<HTMLDivElement>;
    }>;
    autoScroll?: boolean;
    feedback?: boolean;
    /** Skip the Module title bar entirely — for pages whose context is
     *  already named by chrome above (e.g. the stock header). */
    noHeader?: boolean;
};

const noPadding: NoPaddingProps = { source: "body", type: "all" };

const ContentLayout = ({ allowFullscreen, children, help, helpActions, filters, title, modal, stockPage, sonification, autoScroll = false, feedback, noHeader }: Props) => {
    const [disableFilters, setDisableFilters] = useState(false);

    // Offer the page's own tour from its (i) dialog, for every page that has
    // one. Pages mounting a tour with useTour() pass their own action and keep
    // it — they own which of several tours the button should start (the stock
    // Options Platform tab starts the tour for its *active* sub-tab). The rest
    // get it from the route, so a tour added in the admin is offered by its
    // page with no change here.
    //
    // This reaches the pages built on ContentLayout, which is most of them but
    // not the stock tabs — those render their own chrome. The "this page" entry
    // in the Help menu is what covers them, and is the only thing that can:
    // their Help-menu links would need a concrete ticker.
    const pageTour = usePageTour();
    const actions = helpActions ?? (pageTour ? [{ label: "Restart the guided tour", onClick: pageTour.start }] : undefined);

    return (
        <ContentLayoutContext.Provider value={{ disableFilters, setDisableFilters }}>
            <div className={styles.ContentLayout}>
                <Module allowFullscreen={allowFullscreen} filters={filters} help={help} helpActions={actions} title={title} noPadding={noPadding} modal={modal} contentLayout stockPage={stockPage} sonification={sonification} feedback={feedback} noHeader={noHeader}>
                    <Box className={classNames(styles.container, autoScroll ? styles["auto-scroll"] : "")}>{children}</Box>
                </Module>
            </div>
        </ContentLayoutContext.Provider>
    );
};

export default ContentLayout;
